import React, { useState, useEffect } from 'react';
import * as R from 'ramda';
import {
  Datagrid,
  TextField,
  FunctionField,
  useDataProvider,
} from 'react-admin';
import { Box, Card, CardContent, Typography } from '@material-ui/core';
import { DateField } from 'resources/common';
import moment from 'moment-timezone';

const ActivityTab = (props) => {
  const { record } = props;
  const dataProvider = useDataProvider();
  const [sessionEvents, setSessionEvents] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  // Fetch sessions when component mounts or record changes
  useEffect(() => {
    const fetchSessions = async () => {
      if (!record || !record.id) {
        setSessionsLoading(false);
        return;
      }

      try {
        const result = await dataProvider.getStudentSessions('sessions', {
          studentId: record.id,
        });
        const sessions = R.pathOr([], ['data'], result);

        // Transform sessions into connection/disconnection events
        const connectionEvents = R.chain((session) => {
          const events = [];
          // Add connection event (session_start)
          if (session.session_start) {
            events.push({
              id: `session-${session.session_id}-start`,
              event: 'Connected',
              date: session.session_start,
              details: null,
            });
          }
          // Add disconnection event (session_end)
          if (session.session_end) {
            events.push({
              id: `session-${session.session_id}-end`,
              event: 'Disconnected',
              date: session.session_end,
              details: null,
            });
          }
          return events;
        }, sessions);

        setSessionEvents(connectionEvents);
      } catch (error) {
        console.error('Error fetching sessions:', error);
        setSessionEvents([]);
      } finally {
        setSessionsLoading(false);
      }
    };

    fetchSessions();
  }, [record, dataProvider]);

  // Return null if record is not available yet
  if (!record) {
    return null;
  }

  // List of all courses to aggregate events from
  const courses = ['preparation', 'prework', 'course'];

  // Aggregate events from progress (units) across all courses
  const progressEvents = R.pipe(
    R.chain((course) =>
      R.pipe(
        R.pathOr([], [course, 'progress']),
        R.map((entry) => ({
          id: `progress-${course}-${entry.date}`,
          event: 'Unit completed',
          date: entry.date,
          details: entry?.id || null, // No additional info available for unit progress
        })),
      )(record),
    ),
    R.filter(
      (event) =>
        event.date && moment(event.date, moment.ISO_8601, true).isValid(),
    ),
  )(courses);

  // Aggregate events from deliveries (assignments) across all courses
  const deliveryEvents = R.pipe(
    R.chain((course) =>
      R.pipe(
        R.pathOr([], [course, 'deliveries']),
        R.filter(
          (delivery) =>
            delivery.status &&
            !['pending', 'not_delivered'].includes(delivery.status),
        ),
        R.map((entry) => ({
          id: `delivery-${course}-${entry.submit_date}-${entry.title}`,
          event: 'Assignment delivered',
          date: entry.submit_date,
          details: entry.title,
        })),
      )(record),
    ),
    R.filter(
      (event) =>
        event.date && moment(event.date, moment.ISO_8601, true).isValid(),
    ),
  )(courses);

  // Aggregate events from challenges (assessments) across all courses
  const assessmentEvents = R.pipe(
    R.chain((course) =>
      R.pipe(
        R.pathOr([], [course, 'challenges']),
        R.filter(
          (challenge) =>
            challenge.status &&
            !['pending', 'not_delivered'].includes(challenge.status),
        ),
        R.map((entry) => ({
          id: `assessment-${course}-${entry.date}-${entry.assessment_title}`,
          event: 'Assessment completed',
          date: entry.date,
          details: entry.assessment_title,
        })),
      )(record),
    ),
    R.filter(
      (event) =>
        event.date && moment(event.date, moment.ISO_8601, true).isValid(),
    ),
  )(courses);

  // Aggregate events from quizzes across all courses
  const quizEvents = R.pipe(
    R.chain((course) =>
      R.pipe(
        R.pathOr([], [course, 'quizzes']),
        R.map((entry) => ({
          id: `quiz-${course}-${entry.quizId}-${entry.created_at}`,
          event: 'Quiz completed',
          date: entry.created_at,
          details: entry?.quizId || null,
        })),
      )(record),
    ),
    R.filter(
      (event) =>
        event.date && moment(event.date, moment.ISO_8601, true).isValid(),
    ),
  )(courses);

  // Combine all events and sort by date descending
  const allEvents = R.pipe(
    R.concat(progressEvents),
    R.concat(deliveryEvents),
    R.concat(assessmentEvents),
    R.concat(quizEvents),
    R.concat(sessionEvents),
    R.sortBy((event) => -moment(event.date).valueOf()),
  )([], [], [], []);

  // Show loading state while fetching sessions
  if (sessionsLoading) {
    return (
      <Box px="16px">
        <Card style={{ margin: '15px 0px' }}>
          <CardContent>
            <Typography variant="h5" component="h3" gutterBottom>
              Activity
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Loading activity...
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Empty state when no events found
  if (R.isEmpty(allEvents)) {
    return (
      <Box px="16px">
        <Card style={{ margin: '15px 0px' }}>
          <CardContent>
            <Typography variant="h5" component="h3" gutterBottom>
              Activity
            </Typography>
            <Typography variant="body1" color="textSecondary">
              No activity recorded for this student
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Convert array to object for Datagrid (required by react-admin)
  const dataObj = R.indexBy(R.prop('id'), allEvents);

  return (
    <Box px="16px">
      <Card style={{ margin: '15px 0px' }}>
        <CardContent>
          <Typography variant="h5" component="h3" gutterBottom>
            Activity
          </Typography>
          <Datagrid
            data={dataObj}
            ids={allEvents.map((e) => e.id)}
            currentSort={{ field: 'date', order: 'DESC' }}
            total={allEvents.length}
            bulkActionButtons={false}
          >
            <TextField label="Event" source="event" sortable={false} />
            <FunctionField
              label="Details"
              sortBy="details"
              render={(record) => record.details || '-'}
            />
            <FunctionField
              label="Date & Hour"
              sortBy="date"
              render={(record) => (
                <DateField record={record} source="date" showTime={true} />
              )}
            />
          </Datagrid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ActivityTab;
